import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { useToast } from '@/hooks/useToast';
import { doc, getDoc, updateDoc, serverTimestamp, collection, query, where, getDocs } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/lib/firebase';
import type { RouteOffer, OfferRecipient } from '@/types/routeOffers';
import { formatCurrency, formatDate } from '@/lib/utils';
import { Clock, MapPin, Calendar, DollarSign, AlertCircle, CreditCard, ArrowRight, CheckCircle, MessageSquare } from 'lucide-react';

/**
 * Clean object by removing undefined values
 * Firestore rejects documents with undefined field values
 */
function cleanObject(obj: any): any {
  const cleaned: any = {};
  Object.keys(obj).forEach(key => {
    if (obj[key] !== undefined) {
      cleaned[key] = obj[key];
    }
  });
  return cleaned;
}

export default function OfferDetail() {
  const { offerId } = useParams<{ offerId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [offer, setOffer] = useState<RouteOffer | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedWeeks, setSelectedWeeks] = useState<number[]>([]);
  const [currentRecipient, setCurrentRecipient] = useState<OfferRecipient | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [hasExistingEnrollment, setHasExistingEnrollment] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [timingPreferences, setTimingPreferences] = useState('');
  const [submittingPreferences, setSubmittingPreferences] = useState(false);

  // Helper to convert dates
  const toDate = (value: any) => {
    if (!value) return undefined;
    if (typeof value === 'string') return new Date(value);
    if (value.toDate) return value.toDate();
    return value;
  };

  // Load offer and mark as viewed
  useEffect(() => {
    if (!offerId || !user) return;

    const loadOffer = async () => {
      try {
        console.log('[OfferDetail] Loading offer:', offerId);
        setLoading(true);

        const offerDocRef = doc(db, 'routeOffers', offerId);
        const offerDoc = await getDoc(offerDocRef);

        if (!offerDoc.exists()) {
          toast({
            title: 'Offer Not Found',
            description: 'This route offer does not exist.',
            variant: 'destructive',
          });
          navigate('/dashboard');
          return;
        }

        const offerData = offerDoc.data();
        const loadedOffer: RouteOffer = {
          id: offerDoc.id,
          ...offerData,
          expiresAt: toDate(offerData.expiresAt),
          createdAt: toDate(offerData.createdAt),
          updatedAt: toDate(offerData.updatedAt),
          sentAt: toDate(offerData.sentAt),
          filledAt: toDate(offerData.filledAt),
          cancelledAt: toDate(offerData.cancelledAt),
          recipients: offerData.recipients?.map((r: any) => ({
            ...r,
            viewedAt: toDate(r.viewedAt),
            respondedAt: toDate(r.respondedAt),
            paidAt: toDate(r.paidAt),
            notifiedAt: toDate(r.notifiedAt),
          })) || [],
        } as RouteOffer;

        // Find current user's recipient entry
        console.log('[OfferDetail] Looking for recipient with userId:', user.uid);
        console.log('[OfferDetail] Available recipients:', loadedOffer.recipients.map(r => ({ userId: r.userId, email: r.email || r.userEmail })));

        const myRecipient = loadedOffer.recipients.find(r => r.userId === user.uid);

        if (!myRecipient) {
          console.error('[OfferDetail] User not found in recipients list');
          toast({
            title: 'Access Denied',
            description: 'You do not have access to this offer.',
            variant: 'destructive',
          });
          navigate('/dashboard');
          return;
        }

        console.log('[OfferDetail] Found recipient:', myRecipient);
        setOffer(loadedOffer);
        setCurrentRecipient(myRecipient);

        // Initialize selected weeks from recipient data
        setSelectedWeeks(myRecipient.selectedWeeks || []);

        // Check if enrollment already exists (prevents duplicate payments)
        try {
          const enrollmentsRef = collection(db, 'enrollments');
          const enrollmentQuery = query(
            enrollmentsRef,
            where('routeOfferId', '==', offerId),
            where('userId', '==', user.uid)
          );
          const enrollmentSnapshot = await getDocs(enrollmentQuery);

          if (!enrollmentSnapshot.empty) {
            console.log('[OfferDetail] Found existing enrollment, preventing duplicate payment');
            setHasExistingEnrollment(true);
          }
        } catch (enrollmentError: any) {
          console.warn('[OfferDetail] Error checking enrollment (non-fatal):', enrollmentError.message);
        }

        // Auto-mark as viewed if status is pending (non-blocking)
        if (myRecipient.status === 'pending') {
          try {
            const updatedRecipients = loadedOffer.recipients.map(r =>
              r.userId === user.uid
                ? cleanObject({ ...r, status: 'viewed' as const, viewedAt: new Date() })
                : cleanObject(r)
            );

            await updateDoc(offerDocRef, {
              recipients: updatedRecipients,
              updatedAt: serverTimestamp(),
            });

            // Update local state
            setCurrentRecipient({ ...myRecipient, status: 'viewed', viewedAt: new Date() });
            console.log('[OfferDetail] Marked offer as viewed');
          } catch (viewedError: any) {
            // Non-fatal: Log but don't prevent page from loading
            console.warn('[OfferDetail] Failed to mark as viewed (non-fatal):', viewedError.message);
          }
        }
      } catch (error: any) {
        console.error('[OfferDetail] Error loading offer:', error);
        toast({
          title: 'Error',
          description: error.message || 'Failed to load offer. Please try again.',
          variant: 'destructive',
        });
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    loadOffer();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [offerId, user]); // Removed navigate and toast to prevent infinite loop

  // Calculate pricing based on selected weeks
  const pricing = useMemo(() => {
    if (!currentRecipient || selectedWeeks.length === 0) {
      return { subtotal: 0, discount: 0, total: 0 };
    }
    const subtotal = currentRecipient.weeklyRate * currentRecipient.studentCount * selectedWeeks.length;
    const discount = currentRecipient.referralDiscount || 0;
    const total = subtotal - discount;
    return { subtotal, discount, total };
  }, [currentRecipient, selectedWeeks]);

  // Handle week selection toggle
  const toggleWeek = (weekNum: number) => {
    setSelectedWeeks(prev =>
      prev.includes(weekNum)
        ? prev.filter(w => w !== weekNum)
        : [...prev, weekNum].sort((a, b) => a - b)
    );
  };

  // Check if offer is expired
  const isExpired = offer?.expiresAt && new Date(offer.expiresAt) < new Date();

  // Check if user already responded
  const alreadyResponded = currentRecipient?.status === 'declined' || currentRecipient?.status === 'paid';
  const hasAccepted = currentRecipient?.status === 'accepted';
  const canProceedToPayment = hasAccepted && !isExpired && !hasExistingEnrollment;

  // Handle accept offer (without payment)
  const handleAcceptOffer = async () => {
    if (!offer || !currentRecipient || !offerId || !user) return;

    // Validate terms agreement
    if (!agreedToTerms) {
      toast({
        title: 'Terms Required',
        description: 'Please agree to the terms and conditions to continue.',
        variant: 'destructive',
      });
      return;
    }

    // Validate at least one week selected (if partial weeks allowed)
    if (offer.allowPartialWeeks && selectedWeeks.length === 0) {
      toast({
        title: 'No Weeks Selected',
        description: 'Please select at least one week to continue.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);

      const weeksToUse = offer.allowPartialWeeks ? selectedWeeks : offer.availableWeeks;
      const subtotal = currentRecipient.weeklyRate * currentRecipient.studentCount * weeksToUse.length;
      const discount = currentRecipient.referralDiscount || 0;
      const total = subtotal - discount;

      // Update recipient status to 'accepted' (without payment yet)
      const updatedRecipients = offer.recipients.map(r =>
        r.userId === user.uid
          ? cleanObject({
              ...r,
              status: 'accepted' as const,
              selectedWeeks: weeksToUse,
              subtotal,
              totalAmount: total,
              respondedAt: new Date(),
              agreedToTerms: true,
              agreedToTermsAt: new Date(),
            })
          : cleanObject(r)
      );

      const offerDocRef = doc(db, 'routeOffers', offerId);
      await updateDoc(offerDocRef, {
        recipients: updatedRecipients,
        updatedAt: serverTimestamp(),
      });

      toast({
        title: 'Offer Accepted!',
        description: 'Great! Now proceed to payment to complete your enrollment.',
      });

      // Update local state
      setCurrentRecipient({
        ...currentRecipient,
        status: 'accepted',
        selectedWeeks: weeksToUse,
        subtotal,
        totalAmount: total,
        respondedAt: new Date(),
      });
    } catch (error: any) {
      console.error('[OfferDetail] Error accepting offer:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to accept offer. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle proceed to payment
  const handleProceedToPayment = async () => {
    if (!offer || !currentRecipient || !offerId || !user) return;

    // Use selectedWeeks from current recipient if already accepted
    const weeksToUse = currentRecipient.selectedWeeks ||
      (offer.allowPartialWeeks ? selectedWeeks : offer.availableWeeks);

    if (weeksToUse.length === 0) {
      toast({
        title: 'No Weeks Selected',
        description: 'Please select at least one week to continue.',
        variant: 'destructive',
      });
      return;
    }

    try {
      setSubmitting(true);

      // Call Cloud Function to create Stripe checkout session
      const createCheckoutSession = httpsCallable(functions, 'createCheckoutSession');
      const result = await createCheckoutSession({
        offerId,
        selectedWeeks: weeksToUse,
      });

      const data = result.data as { success: boolean; url: string; sessionId: string };

      if (data.success && data.url) {
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        throw new Error('Failed to create checkout session');
      }
    } catch (error: any) {
      console.error('[OfferDetail] Error creating checkout session:', error);

      let errorMessage = 'Failed to start payment process. Please try again.';

      // Handle specific error messages from Cloud Function
      if (error.code === 'functions/failed-precondition') {
        errorMessage = error.message;
      } else if (error.code === 'functions/permission-denied') {
        errorMessage = 'You do not have permission to accept this offer.';
      } else if (error.code === 'functions/not-found') {
        errorMessage = 'This offer could not be found.';
      } else if (error.code === 'functions/invalid-argument') {
        errorMessage = error.message || 'Invalid request. Please check your selection.';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });

      setSubmitting(false);
    }
  };

  // Handle decline offer
  const handleDecline = async () => {
    if (!offer || !currentRecipient || !offerId || !user) return;

    const confirmed = window.confirm(
      'Are you sure you want to decline this offer? This action cannot be undone.'
    );

    if (!confirmed) return;

    try {
      setSubmitting(true);

      // Update recipient in the offer (clean to remove undefined values)
      const updatedRecipients = offer.recipients.map(r =>
        r.userId === user.uid
          ? cleanObject({
              ...r,
              status: 'declined' as const,
              respondedAt: new Date(),
            })
          : cleanObject(r)
      );

      const offerDocRef = doc(db, 'routeOffers', offerId);
      await updateDoc(offerDocRef, {
        recipients: updatedRecipients,
        updatedAt: serverTimestamp(),
      });

      toast({
        title: 'Offer Declined',
        description: 'You have declined this route offer.',
      });

      // Update local state
      setCurrentRecipient({
        ...currentRecipient,
        status: 'declined',
        respondedAt: new Date(),
      });

      // Go back to dashboard
      setTimeout(() => {
        navigate('/dashboard');
      }, 1500);
    } catch (error: any) {
      console.error('[OfferDetail] Error declining offer:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to decline offer. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle timing preferences submission
  const handleSubmitTimingPreferences = async () => {
    if (!timingPreferences.trim() || !offer || !offerId || !user) return;

    try {
      setSubmittingPreferences(true);

      // Update recipient with timing preferences
      const updatedRecipients = offer.recipients.map(r =>
        r.userId === user.uid
          ? cleanObject({ ...r, timingPreferences: timingPreferences.trim() })
          : cleanObject(r)
      );

      const offerDocRef = doc(db, 'routeOffers', offerId);
      await updateDoc(offerDocRef, {
        recipients: updatedRecipients,
        updatedAt: serverTimestamp(),
      });

      // Send email notification
      const sendTimingPreferencesEmail = httpsCallable(functions, 'sendTimingPreferencesEmail');
      await sendTimingPreferencesEmail({
        offerId,
        routeName: offer.routeName,
        userEmail: user.email,
        userName: currentRecipient?.name || user.email,
        timingPreferences: timingPreferences.trim(),
      });

      toast({
        title: 'Preferences Submitted',
        description: "Thank you! We've received your timing preferences and will be in touch.",
      });

      // Update local state
      setCurrentRecipient({
        ...currentRecipient!,
        timingPreferences: timingPreferences.trim(),
      } as any);

      setTimingPreferences('');
    } catch (error: any) {
      console.error('[OfferDetail] Error submitting timing preferences:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to submit preferences. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setSubmittingPreferences(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-shuttlebee-yellow border-t-transparent rounded-full animate-spin" />
          <p className="text-gray-600">Loading offer...</p>
        </div>
      </div>
    );
  }

  if (!offer || !currentRecipient) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{offer.routeName}</h1>
              <p className="text-gray-600 mt-1">
                Route Offer from ShuttleBee
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Button variant="outline" onClick={() => navigate('/dashboard')}>
                Back to Dashboard
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Expiration Warning */}
        {isExpired && (
          <Card className="border-red-200 bg-red-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-red-900 mb-1">This offer has expired</h3>
                  <p className="text-sm text-red-700">
                    This offer expired on {formatDate(offer.expiresAt)}. You can no longer accept it.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Accepted - Awaiting Payment */}
        {hasAccepted && !hasExistingEnrollment && (
          <Card className="border-green-200 bg-green-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-green-900 mb-1">
                    Offer Accepted - Payment Required
                  </h3>
                  <p className="text-sm text-green-700">
                    You accepted this offer on {formatDate(currentRecipient.respondedAt)}.
                    Complete your enrollment by proceeding to payment below.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payment Already Submitted */}
        {hasAccepted && hasExistingEnrollment && (
          <Card className="border-blue-200 bg-blue-50">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h3 className="font-semibold text-blue-900 mb-1">
                    Payment Already Submitted
                  </h3>
                  <p className="text-sm text-blue-700">
                    You have already submitted payment for this offer. Your enrollment is being processed.
                    Check your dashboard for enrollment status updates.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Already Paid or Declined */}
        {alreadyResponded && (
          <Card className={`border-${currentRecipient.status === 'paid' ? 'green' : 'gray'}-200 bg-${currentRecipient.status === 'paid' ? 'green' : 'gray'}-50`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <AlertCircle className={`w-6 h-6 text-${currentRecipient.status === 'paid' ? 'green' : 'gray'}-600 flex-shrink-0 mt-0.5`} />
                <div>
                  <h3 className={`font-semibold text-${currentRecipient.status === 'paid' ? 'green' : 'gray'}-900 mb-1`}>
                    {currentRecipient.status === 'paid' ? 'Enrollment Complete' : 'Offer Declined'}
                  </h3>
                  <p className={`text-sm text-${currentRecipient.status === 'paid' ? 'green' : 'gray'}-700`}>
                    {currentRecipient.status === 'paid'
                      ? `Payment completed on ${formatDate(currentRecipient.paidAt)}. Your spot is confirmed!`
                      : `You declined this offer on ${formatDate(currentRecipient.respondedAt)}.`
                    }
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Route Details */}
        <Card>
          <CardHeader>
            <CardTitle>Route Details</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Pickup Location</p>
                <p className="text-gray-600">{offer.pickupLocation}</p>
                {offer.pickupTime && <p className="text-sm text-gray-500 mt-1">Pickup Time: {offer.pickupTime}</p>}
                {offer.pickupInstructions && (
                  <p className="text-sm text-gray-500 mt-1 italic">{offer.pickupInstructions}</p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <MapPin className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Dropoff Location</p>
                <p className="text-gray-600">{offer.dropoffLocation}</p>
                {offer.dropoffTime && <p className="text-sm text-gray-500 mt-1">Dropoff Time: {offer.dropoffTime}</p>}
                {offer.dropoffInstructions && (
                  <p className="text-sm text-gray-500 mt-1 italic">{offer.dropoffInstructions}</p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Clock className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Offer Expires</p>
                <p className="text-gray-600">{formatDate(offer.expiresAt)}</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-semibold text-gray-900">Rate</p>
                <p className="text-gray-600">{formatCurrency(offer.adjustedWeeklyRate)} per week per student</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Week Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Select Weeks
            </CardTitle>
          </CardHeader>
          <CardContent>
            {offer.allowPartialWeeks ? (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  Select the weeks you'd like transportation for {currentRecipient.studentCount} student{currentRecipient.studentCount > 1 ? 's' : ''}.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-3">
                  {offer.availableWeeks.map((weekNum) => (
                    <button
                      key={weekNum}
                      onClick={() => !alreadyResponded && !isExpired && toggleWeek(weekNum)}
                      disabled={alreadyResponded || isExpired}
                      className={`px-4 py-3 rounded-lg border-2 font-medium transition-all ${
                        selectedWeeks.includes(weekNum)
                          ? 'bg-shuttlebee-yellow border-shuttlebee-dark text-shuttlebee-dark'
                          : 'bg-white border-gray-300 text-gray-700 hover:border-shuttlebee-yellow'
                      } ${(alreadyResponded || isExpired) ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                    >
                      Week {weekNum}
                    </button>
                  ))}
                </div>
                {selectedWeeks.length === 0 && !alreadyResponded && !isExpired && (
                  <p className="text-sm text-amber-600 mt-4 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    Please select at least one week to continue
                  </p>
                )}
              </div>
            ) : (
              <div>
                <p className="text-sm text-gray-600 mb-4">
                  This route requires enrollment for all weeks.
                </p>
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-2">Required Weeks:</p>
                  <p className="text-gray-700">
                    Weeks {offer.availableWeeks.join(', ')} ({offer.availableWeeks.length} weeks total)
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pricing Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Pricing Summary</CardTitle>
          </CardHeader>
          <CardContent>
            {(selectedWeeks.length > 0 || !offer.allowPartialWeeks) ? (
              <div className="space-y-3">
                <div className="flex justify-between text-gray-700">
                  <span>Rate per week per student:</span>
                  <span>{formatCurrency(currentRecipient.weeklyRate)}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Number of students:</span>
                  <span>{currentRecipient.studentCount}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Number of weeks:</span>
                  <span>{offer.allowPartialWeeks ? selectedWeeks.length : offer.availableWeeks.length}</span>
                </div>
                <div className="flex justify-between font-semibold text-gray-900 pt-3 border-t">
                  <span>Subtotal:</span>
                  <span>{formatCurrency(offer.allowPartialWeeks ? pricing.subtotal : currentRecipient.weeklyRate * currentRecipient.studentCount * offer.availableWeeks.length)}</span>
                </div>
                {pricing.discount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Referral Discount:</span>
                    <span>-{formatCurrency(pricing.discount)}</span>
                  </div>
                )}
                <div className="flex justify-between text-2xl font-bold text-shuttlebee-dark pt-3 border-t">
                  <span>Total:</span>
                  <span>{formatCurrency(offer.allowPartialWeeks ? pricing.total : (currentRecipient.weeklyRate * currentRecipient.studentCount * offer.availableWeeks.length) - pricing.discount)}</span>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-center py-8">
                Select weeks to see pricing
              </p>
            )}
          </CardContent>
        </Card>

        {/* Timing Preferences */}
        {!alreadyResponded && !isExpired && (
          <Card className="border-blue-200 bg-blue-50">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <MessageSquare className="w-5 h-5" />
                Timing Preferences
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-blue-900 text-sm">
                We may still add a stop to help fill the vehicle or accommodate additional families.
                If you have timing preferences or conflicts that could affect your participation,
                please let us know below. We'll follow up within two weeks if we're unable to accommodate specific requests.
              </p>

              {currentRecipient?.timingPreferences ? (
                <div className="bg-white border border-blue-200 rounded-lg p-4">
                  <p className="text-sm font-medium text-blue-900 mb-2">Your Timing Preferences:</p>
                  <p className="text-sm text-gray-700">{currentRecipient.timingPreferences}</p>
                </div>
              ) : (
                <>
                  <textarea
                    value={timingPreferences}
                    onChange={(e) => setTimingPreferences(e.target.value)}
                    placeholder="Share any timing preferences, conflicts, or constraints..."
                    rows={4}
                    className="w-full px-4 py-2 border border-blue-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white"
                  />
                  <Button
                    onClick={handleSubmitTimingPreferences}
                    disabled={submittingPreferences || !timingPreferences.trim()}
                    className="w-full bg-blue-600 hover:bg-blue-700"
                  >
                    {submittingPreferences ? 'Submitting...' : 'Submit Preferences'}
                  </Button>
                  <p className="text-xs text-blue-700">
                    This is optional. You can still accept the offer without providing preferences.
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Terms and Conditions */}
        {!alreadyResponded && !isExpired && (
          <Card>
            <CardHeader>
              <CardTitle>Terms and Conditions</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-sm text-gray-700 space-y-3 max-h-60 overflow-y-auto">
                <p className="font-semibold">Payment and Enrollment Terms:</p>
                <ul className="list-disc list-inside space-y-2 ml-2">
                  <li>Payment is required to confirm your enrollment and secure your spot on the route.</li>
                  <li>All sales are final, but seats can be transferred to a student on the same route who is roughly the same age.</li>
                  <li>Route times and stops may be adjusted to accommodate additional families or optimize the route.</li>
                  <li>Transportation services are subject to our full <a href="https://shuttlebee-family-connect.com/terms" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">Terms of Service</a> and <a href="https://shuttlebee-family-connect.com/privacy" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-medium">Privacy Policy</a>.</li>
                  <li>By accepting this offer, you agree to receive communications regarding route updates and enrollment details.</li>
                </ul>
                <p className="text-xs text-gray-600 mt-4">
                  For questions or concerns, contact us at hello@getshuttlebee.com or call 484-324-2559 option 1.
                </p>
              </div>

              <div className="flex items-start gap-3 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <input
                  type="checkbox"
                  id="agree-terms"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-5 h-5 mt-0.5 text-primary border-gray-300 rounded focus:ring-primary cursor-pointer"
                />
                <label htmlFor="agree-terms" className="text-sm text-gray-900 cursor-pointer">
                  <span className="font-semibold">I agree to the terms and conditions</span> and understand that payment is required to confirm my enrollment.
                </label>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action Buttons - Pending/Viewed State */}
        {!alreadyResponded && !hasAccepted && !isExpired && (
          <div className="flex gap-4">
            <Button
              onClick={handleAcceptOffer}
              disabled={submitting || !agreedToTerms || (offer.allowPartialWeeks && selectedWeeks.length === 0)}
              className="flex-1 bg-green-600 hover:bg-green-700"
              size="lg"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  Accepting Offer...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  Accept Offer
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
            <Button
              onClick={handleDecline}
              disabled={submitting}
              variant="outline"
              size="lg"
            >
              Decline Offer
            </Button>
          </div>
        )}

        {/* Action Buttons - Accepted State (Awaiting Payment) */}
        {canProceedToPayment && (
          <>
            {/* Payment Info Banner */}
            <Card className="bg-blue-50 border-blue-200">
              <CardContent className="pt-4">
                <div className="flex items-start gap-3">
                  <CreditCard className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-blue-900 mb-1">
                      Secure Payment with Stripe
                    </p>
                    <p className="text-xs text-blue-700">
                      You'll be redirected to Stripe's secure checkout page to complete your payment.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Button
              onClick={handleProceedToPayment}
              disabled={submitting}
              className="w-full bg-shuttlebee-yellow hover:bg-yellow-500 text-shuttlebee-dark"
              size="lg"
            >
              {submitting ? (
                <span className="flex items-center gap-2">
                  <div className="w-4 h-4 border-2 border-shuttlebee-dark border-t-transparent rounded-full animate-spin" />
                  Redirecting to Payment...
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Proceed to Payment
                  <ArrowRight className="w-4 h-4" />
                </span>
              )}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
